import React, { useCallback, useLayoutEffect, useRef } from 'react';
import { useLocalSearchParams } from 'expo-router';

import { useFeatureFlag } from '@order/LaunchDarkly';

import { useIsLoggedIn } from '../../context/AuthMachine';
import { useRedirectAfterAuthStateChange } from '../../navigation';
import {
  JoinOrSignInScreenContentEmbeddedFrameFlow,
  JoinOrSignInScreenContentHybridFlow,
} from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const JoinOrSignInScreen = () => {
  const isLoggedIn = useIsLoggedIn();
  const redirectAfterAuthStateChange = useRedirectAfterAuthStateChange();
  const { redirect } = useLocalSearchParams<{ redirect?: string }>();

  const isHybridAuthModeEnabled = useFeatureFlag('cels-2041-auth-hybrid-flow');

  // ─── Refs ────────────────────────────────────────────────────────────

  const isLoggedInOnMountRef = useRef(isLoggedIn);

  // ─── Handlers ────────────────────────────────────────────────────────

  /**
   * Redirect customers who open this screen while already signed in.
   *
   * NOTE: Sign-ins that happen on this screen are redirected by `usePostLoginRedirect`.
   */
  const redirectSignedInCustomer = useCallback(() => {
    if (!isLoggedInOnMountRef.current) {
      return;
    }

    redirectAfterAuthStateChange(redirect);
  }, [redirect, redirectAfterAuthStateChange]);

  // ─── Effects ─────────────────────────────────────────────────────────

  useLayoutEffect(redirectSignedInCustomer, [redirectSignedInCustomer]);

  // ─────────────────────────────────────────────────────────────────────

  if (isLoggedIn) {
    return null;
  }

  if (isHybridAuthModeEnabled) {
    return <JoinOrSignInScreenContentHybridFlow />;
  }

  return <JoinOrSignInScreenContentEmbeddedFrameFlow />;
};
